import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import PageHeader from "app/shared/PageHeader";
import { Tabs, Tag } from "antd";
import {
    CheckCircleFilled,
    CloseCircleFilled,
    InfoCircleFilled,
    ExclamationCircleFilled
} from "@ant-design/icons";
import { AgTableClient } from "app/shared/AgTable";
import sampleDailyReconData from "./sampleDailyRecon.json";
import sampleFraudDetectionData from "./sampleFraudDetection.json";
import sampleAuthFailureData from "./sampleAuthFailure.json";
import moment from "moment";

export default function VCC() {
    const [activeKey, setActiveKey] = useState("1");
    const gridRef = useRef<any>(null);
    const updateIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const items = [
        {
            key: "1",
            label: "Daily Recon",
        },
        {
            key: "2",
            label: "Fraud Detection ",
        },
        // {
        //     key: "3",
        //     label: "VAN Auth Failure ",
        // },
    ];

    // Memoized cellRenderer functions to prevent flickering
    // Helper function to parse date value to Date object for filtering
    const parseDateForFilter = useCallback((dateValue: any) => {
        if (!dateValue) return null;
        let parsedDate;

        // Handle timestamp (milliseconds since epoch) - numbers > 1000000000000 are likely timestamps
        if (typeof dateValue === "number" && dateValue > 1000000000000) {
            // It's a timestamp in milliseconds
            return new Date(dateValue);
        } else if (typeof dateValue === "string" && dateValue.includes(" ")) {
            // Format: "YYYYMMDD HH:mm:ss"
            parsedDate = moment(dateValue, "YYYYMMDD HH:mm:ss");
            if (!parsedDate.isValid()) {
                parsedDate = moment(dateValue);
            }
        } else if (typeof dateValue === "number") {
            // Format: 20251229 (YYYYMMDD as number)
            parsedDate = moment(dateValue.toString(), "YYYYMMDD");
        } else if (typeof dateValue === "string") {
            // Try parsing as standard date string
            parsedDate = moment(dateValue);
        } else {
            parsedDate = moment(dateValue);
        }
        return parsedDate.isValid() ? parsedDate.toDate() : null;
    }, []);

    const dateCellRenderer = useCallback((params: any) => {
        const dateValue = params.value;
        if (!dateValue) return "-";
        let parsedDate;

        // Handle timestamp (milliseconds since epoch)
        if (typeof dateValue === "number" && dateValue > 1000000000000) {
            parsedDate = moment(dateValue);
        } else if (typeof dateValue === "string" && dateValue.includes(" ")) {
            parsedDate = moment(dateValue, "YYYYMMDD HH:mm:ss");
            if (!parsedDate.isValid()) {
                parsedDate = moment(dateValue);
            }
        } else if (typeof dateValue === "number") {
            // Format: 20251229 (YYYYMMDD as number)
            parsedDate = moment(dateValue.toString(), "YYYYMMDD");
        } else if (typeof dateValue === "string") {
            parsedDate = moment(dateValue);
        } else {
            parsedDate = moment(dateValue);
        }
        return parsedDate.isValid() ? parsedDate.format("DD-MM-YYYY") : dateValue;
    }, []);

    const dateCellRendererSimple = useCallback((params: any) => {
        const dateValue = params.value;
        if (!dateValue) return "-";
        let parsedDate;

        // Handle timestamp (milliseconds since epoch)
        if (typeof dateValue === "number" && dateValue > 1000000000000) {
            parsedDate = moment(dateValue);
        } else if (typeof dateValue === "string" && dateValue.includes(" ")) {
            parsedDate = moment(dateValue, "YYYYMMDD HH:mm:ss");
        } else if (typeof dateValue === "number") {
            // Format: 20251229 (YYYYMMDD as number)
            parsedDate = moment(dateValue.toString(), "YYYYMMDD");
        } else {
            parsedDate = moment(dateValue);
        }
        return parsedDate.isValid() ? parsedDate.format("DD-MM-YYYY") : dateValue;
    }, []);

    // Filter value getter for date columns - returns Date object for AG Grid filtering
    const dateFilterValueGetter = useCallback((params: any) => {
        return parseDateForFilter(params.data[params.colDef.field]);
    }, [parseDateForFilter]);

    // Date filter comparator for AG Grid date filtering
    const dateFilterComparator = useCallback((filterDate: Date, cellValue: any) => {
        if (!cellValue) return -1;

        // Parse the cell value to a Date object
        const cellDate = parseDateForFilter(cellValue);
        if (!cellDate) return -1;

        // Normalize times to midnight for date-only comparison
        const filterDateOnly = new Date(
            filterDate.getFullYear(),
            filterDate.getMonth(),
            filterDate.getDate()
        );
        const cellDateOnly = new Date(
            cellDate.getFullYear(),
            cellDate.getMonth(),
            cellDate.getDate()
        );

        // Compare dates
        if (cellDateOnly < filterDateOnly) return -1;
        if (cellDateOnly > filterDateOnly) return 1;
        return 0;
    }, [parseDateForFilter]);

    // Date filter params for AG Grid
    const dateFilterParams = useMemo(() => ({
        comparator: dateFilterComparator,
    }), [dateFilterComparator]);

    const reconStatusCellRenderer = useCallback((params: any) => {
        const status = params.value;
        if (!status) return <Tag>-</Tag>;

        const statusLower = status.toString().toLowerCase();
        let tagColor = "red";

        if (statusLower === "reconciled") {
            tagColor = "green";
        } else if (statusLower === "undercharged") {
            tagColor = "orange";
        } else if (statusLower === "booking_missing") {
            tagColor = "gold";
        }

        return <Tag color={tagColor}>{status}</Tag>;
    }, []);

    const suspicionLevelCellRenderer = useCallback((params: any) => {
        const level = params.value;
        if (!level) return <Tag>-</Tag>;

        const levelUpper = level.toString().toUpperCase();
        let tagColor = "red";

        if (levelUpper === "GENUINE" || levelUpper === "LOW") {
            tagColor = "green";
        } else if (levelUpper === "MEDIUM") {
            tagColor = "gold";
        } else if (levelUpper === "HIGH") {
            tagColor = "red";
        }

        return <Tag color={tagColor}>{level}</Tag>;
    }, []);

    const statusCellRenderer = useCallback((params: any) => {
        const status = params.value;
        if (!status) return <Tag>-</Tag>;

        const statusUpper = status.toString().toUpperCase();
        let backgroundColor = "#f0f0f0";
        let borderColor = "#d9d9d9";
        let textColor = "#595959";
        let IconComponent: any = null;

        if (statusUpper === "AUTHORISED" || statusUpper === "AUTHORIZED" || statusUpper === "ACTIVE" || statusUpper === "OPEN") {
            backgroundColor = "#f6ffed";
            borderColor = "#b7eb8f";
            textColor = "#389e0d";
            IconComponent = CheckCircleFilled;
        } else if (statusUpper === "ISSUED") {
            backgroundColor = "#e6f7ff";
            borderColor = "#bae7ff";
            textColor = "#40a9ff";
            IconComponent = InfoCircleFilled;
        } else if (statusUpper === "CLOSED") {
            backgroundColor = "#fff7e6";
            borderColor = "#ffd591";
            textColor = "#ffa940";
            IconComponent = CloseCircleFilled;
        } else if (statusUpper === "CANCELLED") {
            backgroundColor = "#fff1f0";
            borderColor = "#ffa39e";
            textColor = "#cf1322";
            IconComponent = CloseCircleFilled;
        } else if (statusUpper === "PENDING") {
            backgroundColor = "#fffbe6";
            borderColor = "#ffe58f";
            textColor = "#d48806";
            IconComponent = ExclamationCircleFilled;
        }

        return (
            <Tag
                style={{
                    backgroundColor: backgroundColor,
                    border: `1px solid ${borderColor}`,
                    color: textColor,
                    fontWeight: 400,
                    borderRadius: "4px",
                    padding: "4px",
                    fontSize: "11px",
                    lineHeight: "13px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px"
                }}
            >
                {IconComponent && <IconComponent style={{ fontSize: "14px", fontWeight: 400 }} />}
                <span>{status}</span>
            </Tag>
        );
    }, []);

    const typeCellRenderer = useCallback((params: any) => {
        // Always display "FK" for all rows in fraud detection
        return "FK";
    }, []);

    const serialNumberCellRenderer = useCallback((params: any) => {
        // Return row index + 1 for serial number
        return (params.node?.rowIndex ?? 0) + 1;
    }, []);

    const yesNoCellRenderer = useCallback((params: any) => {
        const value = params.value;
        if (value === "TRUE" || value === "true" || value === 1) {
            return "YES";
        } else if (value ==="FALSE" || value === "false" || value === 0) {
            return "NO";
        }
        return value || "-";
    }, []);

    // Column definitions
    const columnDefs = useMemo(() => {
        if (activeKey === "1") {
            // Daily Recon columns based on sampleDailyRecon.json
            return [
                {
                    field: "S.No",
                    headerName: "S.No",
                    width: 80,
                    pinned: "left",
                    cellRenderer: serialNumberCellRenderer,
                    sortable: false,
                    filter: false,
                    enableRowGroup: false,
                    lockPosition: true
                },
                {
                    field: "TBOMRefNO_Provider",
                    headerName: "Ref NO (Provider)",
                    width: 140,
                    enableRowGroup: true,
                    filter: "agTextColumnFilter"
                },
                {
                    field: "VANTransactionID",
                    headerName: "VAN Transaction ID",
                    width: 140,
                    enableRowGroup: true,
                    filter: "agTextColumnFilter"
                },
                {
                    field: "Recon_status",
                    headerName: "Recon Status",
                    width: 130,
                    enableRowGroup: true,
                    cellRenderer: reconStatusCellRenderer
                },
                {
                    field: "NetPayableAmount",
                    headerName: "Net Payable Amount",
                    width: 150,
                    formatType: "NUMBER_WithOUT_INR",
                    enableRowGroup: true,
                    enableValue: true,
                    filter: "agNumberColumnFilter",
                  
                },
                {
                    field: "PayableAmountCurr",
                    headerName: "Payable Amount Currency",
                    width: 130,
                    formatType: "CURRENCY",
                    enableRowGroup: true
                },
                {
                    field: "NetPosAmt",
                    headerName: "Net POS Amt",
                    width: 130,
                    formatType: "NUMBER_WithOUT_INR",
                    enableRowGroup: true,
                    enableValue: true,
                    filter: "agNumberColumnFilter"
                },
                {
                    field: "POSCurr",
                    headerName: "POS Currency",
                    width: 150,
                    formatType: "CURRENCY",
                    enableRowGroup: true
                },
                {
                    field: "diffAmt",
                    headerName: "Diff Amount",
                    width: 150,
                    formatType: "NUMBER_WithOUT_INR",
                    enableRowGroup: true,
                    enableValue: true,
                    filter: "agNumberColumnFilter"
                },
                // {
                //     field: "diffAmt_USD",
                //     headerName: "Diff Amount (USD)",
                //     width: 150,
                //     formatType: "NUMBER_WITH_USD",
                //     enableRowGroup: true,
                //     enableValue: true,
                //     filter: "agNumberColumnFilter"
                // },

                {
                    headerName: "Supplier Details",
                    children: [
                        {
                            field: "SupplierName",
                            headerName: "Supplier Name",
                            width: 200,
                            formatType: "ENTITY_WITH_LOGO",
                            enableRowGroup: true,
                            filter: "agTextColumnFilter",
                            columnGroupShow: "close",
                        },
                        {
                            field: "SupplierCode",
                            headerName: "Supplier Code",
                            width: 140,
                            enableRowGroup: true,
                            filter: "agTextColumnFilter",
                            columnGroupShow: "open",

                        },
                        {
                            field: "InvoiceNumber",
                            headerName: "Invoice Number",
                            width: 140,
                            enableRowGroup: true,
                            filter: "agTextColumnFilter",
                            columnGroupShow: "open",
                        },
                    ]
                },
                {
                    field: "NetTransactionAmt",
                    headerName: "Net Transaction Amt",
                    width: 200,
                    formatType: "AMOUNT",
                    enableRowGroup: true,
                    enableValue: true,
                    filter: "agNumberColumnFilter",
                    enableCellChangeFlash: true
                },
                {
                    field: "TransactionCurrency",
                    headerName: "Transaction Currency",
                    width: 180,
                    formatType: "CURRENCY",
                    enableRowGroup: true
                },
                {
                    field: "ROE",
                    headerName: "ROE",
                    width: 130,
                    enableRowGroup: true,
                    filter: "agNumberColumnFilter"
                },
                // { field: "TBOMRefNO_Internal", headerName: "Ref NO (Internal)", width: 200 },
                {
                    field: "PayableAmount",
                    headerName: "Payable Amount",
                    width: 150,
                    formatType: "AMOUNT",
                    enableRowGroup: true,
                    filter: "agNumberColumnFilter",
                    enableValue: true,
                    enableCellChangeFlash: true
                },
                {
                    field: "CreditedAmount",
                    headerName: "Credited Amount",
                    width: 150,
                    formatType: "AMOUNT",
                    enableRowGroup: true,
                    enableValue: true,
                    filter: "agNumberColumnFilter"
                },
               
               
                {
                    field: "NetPayableAmount_USD",
                    headerName: "Net Payable Amount (USD)",
                    width: 150,
                    formatType: "NUMBER_WITH_USD",
                    enableRowGroup: true,
                    enableValue: true,
                    filter: "agNumberColumnFilter"
                },
               
                {
                    field: "currmatched",
                    headerName: "Currency Matched",
                    width: 150,
                    formatType: "MATCH_MISMATCH",
                    enableRowGroup: true
                },
                {
                    field: "Multiple_Attempts",
                    headerName: "Multiple Attempts",
                    width: 130,
                    //formatType: "YES_NO",
                    enableRowGroup: true,
                    cellRenderer: yesNoCellRenderer
                },
                {
                    field: "Multiple_merchants",
                    headerName: "Multiple Merchants",
                    width: 130,
                    formatType: "TRUE_FALSE",
                    enableRowGroup: true
                },
                {
                    field: "ReportDate",
                    headerName: "Report Date",
                    width: 150,
                    enableRowGroup: true,
                    filter: "agDateColumnFilter",
                    filterParams: dateFilterParams,
                    cellRenderer: dateCellRenderer,
                    filterValueGetter: dateFilterValueGetter
                },
               
                {
                    field: "SuspicionLevel",
                    headerName: "Suspicion Level",
                    width: 130,
                    enableRowGroup: true,
                    cellRenderer: suspicionLevelCellRenderer
                },

                {
                    field: "isReviewed",
                    headerName: "Is Reviewed",
                    width: 130,
                    formatType: "TRUE_FALSE"
                },
            ];
        } else if (activeKey === "2") {
            // Fraud Detection Report columns based on sampleFraudDetection.json
            return [
                {
                    field: "S.No",
                    headerName: "S.No",
                    width: 80,
                    pinned: "left",
                    cellRenderer: serialNumberCellRenderer,
                    sortable: false,
                    filter: false,
                    enableRowGroup: false,
                    lockPosition: true
                },
                {
                    field: "VANhistoryID",
                    headerName: "VAN History ID",
                    width: 130,
                    enableRowGroup: true,
                    filter: "agTextColumnFilter"
                },
                {
                    field: "VANTransactionID",
                    headerName: "VAN Transaction ID",
                    width: 150,
                    enableRowGroup: true,
                    filter: "agTextColumnFilter"
                },
                {
                    field: "TBOMRefNO_Provider",
                    headerName: "Ref NO (Provider)",
                    width: 150,
                    enableRowGroup: true,
                    filter: "agTextColumnFilter"
                },
                {
                    field: "suspicion_level",
                    headerName: "Suspicion Level",
                    width: 150,
                    enableRowGroup: true,
                    //filter:"agTextColumnFilter",
                    cellRenderer: suspicionLevelCellRenderer
                },
                {
                    field: "suspicion_rate",
                    headerName: "Suspicion Rate",
                    width: 150,
                    formatType: "RATE",
                    enableRowGroup: true
                },
                {
                    field: "currmatched",
                    headerName: "Currency Matched",
                    width: 150,
                    formatType: "MATCH_MISMATCH",
                    enableRowGroup: true
                },
                {
                    field: "AmountwithinExpectedRange",
                    headerName: "Amount Within Expected Range",
                    width: 220,
                    formatType: "TRUE_FALSE",
                    enableRowGroup: true
                },
                {
                    field: "Namematched",
                    headerName: "Name Matched",
                    width: 130,
                    formatType: "MATCH_MISMATCH",
                    enableRowGroup: true
                },
                {
                    field: "FraudMerchantFlag",
                    headerName: "Fraud Merchant Flag",
                    width: 180,
                    //formatType: "TRUE_FALSE",
                    enableRowGroup: true
                },
                {
                    field: "CityMatched",
                    headerName: "City Matched",
                    width: 130,
                    formatType: "TRUE_FALSE",
                    enableRowGroup: true
                },
               
                {
                    headerName: "Supplier Details",
                    children: [
                        {
                            field: "SupplierName", headerName: "Supplier Name", width: 200,
                            formatType: "ENTITY_WITH_LOGO",
                            enableRowGroup: true,
                            filter: "agTextColumnFilter",
                            columnGroupShow: "close",
                        },

                        {
                            field: "SupplierCode",
                            headerName: "Supplier Code",
                            width: 150,
                            enableRowGroup: true,
                            filter: "agTextColumnFilter",
                            columnGroupShow: "open",
                        },
                        {
                            field: "SupplierAmt",
                            headerName: "Supplier Amount",
                            width: 150,
                            enableRowGroup: true,
                            formatType: "NUMBER_WithOUT_INR",
                            filter: "agNumberColumnFilter",
                            columnGroupShow: "open",
                        },
                        {
                            field: "SupplierCurr",
                            headerName: "Supplier Currency",
                            width: 150,
                            enableRowGroup: true,
                            formatType: "CURRENCY",
                            //filter:"agTextColumnFilter",
                            columnGroupShow: "open",
                        },
                        
                        {
                            field: "InvoiceNumber",
                            headerName: "Invoice Number",
                            width: 150,
                            enableRowGroup: true,
                            filter: "agTextColumnFilter",
                            columnGroupShow: "open",
                        },
                    ]
                },
               
                {
                    headerName: "Merchant Details",
                    children: [
                        

                        {
                            field: "MerchCategoryName",
                            headerName: "Merchant Category Name",
                            width: 220,
                            enableRowGroup: true,
                            filter: "agTextColumnFilter",
                            formatType: "ENTITY_WITH_LOGO",
                            columnGroupShow: "close",
                        },
                        {
                            field: "MerchantName",
                            headerName: "Merchant Name",
                            width: 200,
                            enableRowGroup: true,
                            // formatType: "ENTITY",
                            filter: "agTextColumnFilter",
                            columnGroupShow: "close",
                        },
                        {
                            field: "MerchantCountry",
                            headerName: "Merchant Country",
                            width: 150,
                            enableRowGroup: true,
                            //filter:"agTextColumnFilter",
                            formatType: "COUNTRY",
                            columnGroupShow: "close",
                        },
                        {
                            field: "MerchantAddress",
                            headerName: "Merchant Address",
                            width: 200,
                            enableRowGroup: true,
                            filter: "agTextColumnFilter",
                            columnGroupShow: "open",
                        },
                        {
                            field: "MerchantID",
                            headerName: "Merchant ID",
                            width: 150,
                            enableRowGroup: true,
                            filter: "agNumberColumnFilter",
                            columnGroupShow: "open",
                        },
                        {
                            field: "MerchCategoryCode",
                            headerName: "Merchant Category Code",
                            width: 200,
                            enableRowGroup: true,
                            filter: "agNumberColumnFilter",
                            columnGroupShow: "open",
        
                        },
                        {
                            field: "MerchantState",
                            headerName: "Merchant State",
                            width: 150,
                            enableRowGroup: true,
                            filter: "agTextColumnFilter",
                            columnGroupShow: "open",
                        },
                        {
                            field: "MerchantCity",
                            headerName: "Merchant City",
                            width: 150,
                            enableRowGroup: true,
                            filter: "agTextColumnFilter",
                            columnGroupShow: "open",
                        },
                    ]
                },
                // { field: "ConfirmationNO", headerName: "Confirmation NO", width: 180 },
               
                {headerName:"Hotel Details",
                    children:[
                       
                        {
                            field: "HotelName_Provider",
                            headerName: "Hotel Name (Provider)",
                            width: 200,
                            enableRowGroup: true,
                            filter: "agTextColumnFilter",
                            formatType: "ENTITY_WITH_LOGO",
                            columnGroupShow: "close",
                        },
                        {
                            field: "HotelName_Internal",
                            headerName: "Hotel Name (Internal)",
                            width: 215,
                            enableRowGroup: true,
                            filter: "agTextColumnFilter",
                            formatType: "ENTITY_WITH_LOGO",
                            columnGroupShow: "close",
                        },
                        {
                            field: "countrycode",
                            headerName: "Country Code",
                            width: 130,
                            enableRowGroup: true,
                            formatType: "COUNTRY",
                            columnGroupShow: "close",
                            //filter:"agTextColumnFilter"
                        },
                        {
                            field: "HotelCity",
                            headerName: "Hotel City",
                            width: 150,
                            enableRowGroup: true,
                            filter: "agTextColumnFilter",
                            columnGroupShow: "open",
                        },
                        {
                            field: "cityCode",
                            headerName: "City Code",
                            width: 120,
                            enableRowGroup: true,
                            filter: "agNumberColumnFilter",
                            columnGroupShow: "open",
                        },
                        {
                            field: "hotelID",
                            headerName: "Hotel ID",
                            width: 120,
                            enableRowGroup: true,
                            filter: "agNumberColumnFilter",
                            columnGroupShow: "open",
                        },
                        {
                            field: "address_l1",
                            headerName: "Address Line 1",
                            width: 300,
                            enableRowGroup: true,
                            filter: "agTextColumnFilter",
                            columnGroupShow: "open",
                        },
                       
                    ]
                },
               
                {
                    field: "VAN",
                    headerName: "VAN",
                    width: 180,
                    filter: "agTextColumnFilter",
                    enableRowGroup: true,
                },
               
                {
                    field: "VANAmt",
                    headerName: "VAN Amount",
                    width: 150,
                    enableRowGroup: true,
                    formatType: "NUMBER_WithOUT_INR",
                    filter: "agNumberColumnFilter"
                },
                {
                    field: "VANcurr",
                    headerName: "VAN Currency",
                    width: 130,
                    formatType: "CURRENCY",
                    enableRowGroup: true,
                    filter: "agTextColumnFilter"
                },
                {
                    field: "VCA_No", headerName: "VCA No", width: 120,
                    enableRowGroup: true,
                    filter: "agNumberColumnFilter",
                },
                {
                    field: "Type",
                    headerName: "Type",
                    width: 120,
                    enableRowGroup: true,
                    filter: "agTextColumnFilter",
                    cellRenderer: typeCellRenderer
                },
                
                {
                    field: "VCA_Amt",
                    headerName: "VCA Amount",
                    width: 150,
                    enableRowGroup: true,
                    formatType: "NUMBER_WithOUT_INR",
                    filter: "agNumberColumnFilter"
                },
                {
                    field: "VCA_Curr",
                    headerName: "VCA Currency",
                    width: 130,
                    enableRowGroup: true,
                    formatType: "CURRENCY",

                },
                // { field: "CBFeeOnVanAmount", headerName: "CB Fee On VAN Amount", width: 200, type: "numericColumn" },
                {
                    field: "VANVisibleExpiryDate",
                    headerName: "VAN Visible Expiry Date",
                    width: 200,
                    enableRowGroup: true,
                    filter: "agDateColumnFilter",
                    filterParams: dateFilterParams,
                    cellRenderer: dateCellRenderer,
                    filterValueGetter: dateFilterValueGetter
                },
                {
                    field: "VANRequestedDate",
                    headerName: "VAN Requested Date",
                    width: 180,
                    enableRowGroup: true,
                    filter: "agDateColumnFilter",
                    filterParams: dateFilterParams,
                    cellRenderer: dateCellRenderer,
                    filterValueGetter: dateFilterValueGetter
                },
                {
                    field: "VANActivationDate",
                    headerName: "VAN Activation Date",
                    width: 180,
                    enableRowGroup: true,
                    filter: "agDateColumnFilter",
                    filterParams: dateFilterParams,
                    cellRenderer: dateCellRenderer,
                    filterValueGetter: dateFilterValueGetter
                },
                {
                    field: "VANValidUntilDate",
                    headerName: "VAN Valid Until Date",
                    width: 180,
                    enableRowGroup: true,
                    filter: "agDateColumnFilter",
                    filterParams: dateFilterParams,
                    cellRenderer: dateCellRenderer,
                    filterValueGetter: dateFilterValueGetter
                },
                {
                    field: "CheckoutDate",
                    headerName: "Checkout Date",
                    width: 150,
                    enableRowGroup: true,
                    filter: "agDateColumnFilter",
                    filterParams: dateFilterParams,
                    cellRenderer: dateCellRenderer,
                    filterValueGetter: dateFilterValueGetter
                },
                {
                    field: "checkInDate",
                    headerName: "Check In Date",
                    width: 150,
                    enableRowGroup: true,
                    filter: "agDateColumnFilter",
                    filterParams: dateFilterParams,
                    cellRenderer: dateCellRenderer,
                    filterValueGetter: dateFilterValueGetter
                },
                {
                    field: "ActivityType",
                    headerName: "Activity Type",
                    width: 150,
                    enableRowGroup: true,
                    filter: "agTextColumnFilter"
                },
                // { field: "Processed", headerName: "Processed", width: 120 },



                {
                    field: "MaxAmt",
                    headerName: "Max Amount",
                    width: 130,
                    enableRowGroup: true,
                    formatType: "NUMBER_WithOUT_INR",
                    filter: "agNumberColumnFilter"
                },
                {
                    field: "POSAmt",
                    headerName: "POS Amount",
                    width: 130,
                    enableRowGroup: true,
                    formatType: "NUMBER_WithOUT_INR",
                    filter: "agNumberColumnFilter"
                },
                {
                    field: "POSCurr",
                    headerName: "POS Currency",
                    width: 130,
                    enableRowGroup: true,
                    formatType: "CURRENCY",
                    filter: "agTextColumnFilter"
                },
                {
                    field: "ROE",
                    headerName: "ROE",
                    width: 120,
                    enableRowGroup: true,
                    filter: "agNumberColumnFilter"
                },
                {
                    field: "RateofExchange",
                    headerName: "Rate of Exchange",
                    width: 150,
                    enableRowGroup: true,
                    formatType: "RATE",
                    filter: "agNumberColumnFilter"
                },
                {
                    field: "TransactionAmount",
                    headerName: "Transaction Amount",
                    width: 180,
                    enableRowGroup: true,
                    formatType: "NUMBER_WithOUT_INR",
                    filter: "agNumberColumnFilter"
                },
                {
                    field: "TransactionCurrency",
                    headerName: "Transaction Currency",
                    width: 180,
                    enableRowGroup: true,
                    formatType: "CURRENCY",
                    filter: "agTextColumnFilter"
                },
                {
                    field: "TransactionDate",
                    headerName: "Transaction Date",
                    width: 150,
                    enableRowGroup: true,
                    filter: "agDateColumnFilter",
                    filterParams: dateFilterParams,
                    cellRenderer: dateCellRenderer,
                    filterValueGetter: dateFilterValueGetter
                },
                {
                    field: "TransactionTime",
                    headerName: "Transaction Time",
                    width: 150,
                    enableRowGroup: true,
                    filter: "agTextColumnFilter"
                },
                {
                    field: "ReconciliationAmount",
                    headerName: "Reconciliation Amount",
                    width: 200,
                    enableRowGroup: true,
                    formatType: "NUMBER_WithOUT_INR",
                    filter: "agNumberColumnFilter"
                },
                {
                    field: "ReconciliationCurrency",
                    headerName: "Reconciliation Currency",
                    width: 200,
                    enableRowGroup: true,
                    formatType: "CURRENCY",
                    filter: "agTextColumnFilter"
                },
                // { field: "CBFeeOnReconciliationAmount", headerName: "CB Fee On Reconciliation Amount", width: 250, type: "numericColumn" },
               

              
              
               
                {
                    field: "AcquirerID",
                    headerName: "Acquirer ID",
                    width: 130,
                    enableRowGroup: true,
                    filter: "agNumberColumnFilter"
                },
                
               
               
               
                {
                    field: "ReportDate",
                    headerName: "Report Date",
                    width: 150,
                    enableRowGroup: true,
                    filter: "agDateColumnFilter",
                    filterParams: dateFilterParams,
                    cellRenderer: dateCellRenderer,
                    filterValueGetter: dateFilterValueGetter
                },
            ];
        } else {
            // VAN Auth Failure Report columns based on sampleAuthFailure.json
            return [
                {
                    field: "TransID",
                    headerName: "Transaction ID",
                    width: 150,
                    enableRowGroup: true,
                    filter: "agNumberColumnFilter"
                },
                {
                    field: "Status",
                    headerName: "Status",
                    width: 120,
                    enableRowGroup: true,
                    //filter:"agTextColumnFilter",
                    cellRenderer: statusCellRenderer
                },
                {
                    field: "IssuedToECN",
                    headerName: "Issued To ECN",
                    width: 150,
                    enableRowGroup: true,
                    filter: "agNumberColumnFilter"
                },
                {
                    field: "CardType",
                    headerName: "Card Type",
                    width: 120,
                    enableRowGroup: true,
                    //filter:"agTextColumnFilter"
                },
                // { field: "MerchantCategory", 
                //     headerName: "Merchant Category", 
                //     width: 180,
                //     enableRowGroup:true,
                //     filter:"agTextColumnFilter"
                // },
                {
                    field: "IntegratorName",
                    headerName: "Integrator Name",
                    width: 180,
                    enableRowGroup: true,
                    filter: "agTextColumnFilter"
                },
                {
                    field: "VAN",
                    headerName: "VAN",
                    width: 180,
                    enableRowGroup: true,
                    filter: "agTextColumnFilter"
                },
                {
                    field: "RequestedDate",
                    headerName: "Requested Date",
                    width: 180,
                    enableRowGroup: true,
                    filter: "agDateColumnFilter",
                    filterParams: dateFilterParams,
                    cellRenderer: dateCellRendererSimple,
                    filterValueGetter: dateFilterValueGetter
                },
                {
                    field: "ActivationDate",
                    headerName: "Activation Date",
                    width: 150,
                    enableRowGroup: true,
                    filter: "agDateColumnFilter",
                    filterParams: dateFilterParams,
                    cellRenderer: dateCellRendererSimple,
                    filterValueGetter: dateFilterValueGetter
                },
                {
                    field: "ExpiryDate",
                    headerName: "Expiry Date",
                    width: 130,
                    enableRowGroup: true,
                    filter: "agDateColumnFilter",
                    filterParams: dateFilterParams,
                    cellRenderer: dateCellRendererSimple,
                    filterValueGetter: dateFilterValueGetter
                },
                {
                    field: "VANCurrency",
                    headerName: "VAN Currency",
                    width: 130,
                    enableRowGroup: true,
                    formatType: "CURRENCY",
                    filter: "agTextColumnFilter"
                },
                {
                    field: "MinAmt",
                    headerName: "Min Amount",
                    width: 130,
                    enableRowGroup: true,
                    formatType: "AMOUNT",
                    filter: "agNumberColumnFilter"
                },
                {
                    field: "MaxAmt",
                    headerName: "Max Amount",
                    width: 130,
                    enableRowGroup: true,
                    formatType: "AMOUNT",
                    filter: "agNumberColumnFilter"
                },
                {
                    field: "NetSettledAmt",
                    headerName: "Net Settled Amount",
                    width: 180,
                    enableRowGroup: true,
                    formatType: "AMOUNT",
                    filter: "agNumberColumnFilter"
                },
                {
                    field: "RefundAmount",
                    headerName: "Refund Amount",
                    width: 150,
                    enableRowGroup: true,
                    formatType: "AMOUNT",
                    filter: "agNumberColumnFilter"
                },
                {
                    field: "AvailableBalance",
                    headerName: "Available Balance",
                    width: 160,
                    enableRowGroup: true,
                    formatType: "AMOUNT",
                    filter: "agNumberColumnFilter"
                },
                {
                    field: "AuthCount",
                    headerName: "Auth Count",
                    width: 120,
                    enableRowGroup: true,
                    filter: "agNumberColumnFilter"
                },
                {
                    field: "VANMode",
                    headerName: "VAN Mode",
                    width: 130,
                    enableRowGroup: true,
                    filter: "agTextColumnFilter"
                },
                {
                    field: "VANhistoryID",
                    headerName: "VAN History ID",
                    width: 150,
                    enableRowGroup: true,
                    filter: "agNumberColumnFilter"
                },
                {
                    field: "ActivityType",
                    headerName: "Activity Type",
                    width: 150,
                    enableRowGroup: true,
                    filter: "agTextColumnFilter"
                },
                {
                    field: "TransDate",
                    headerName: "Transaction Date",
                    width: 180,
                    enableRowGroup: true,
                    filter: "agDateColumnFilter",
                    filterParams: dateFilterParams,
                    cellRenderer: dateCellRendererSimple,
                    filterValueGetter: dateFilterValueGetter
                },
                {
                    field: "TransCurr",
                    headerName: "Transaction Currency",
                    width: 180,
                    enableRowGroup: true,
                    formatType: "CURRENCY",
                    filter: "agTextColumnFilter"
                },
                {
                    field: "TransAmt",
                    headerName: "Transaction Amount",
                    width: 180,
                    enableRowGroup: true,
                    formatType: "AMOUNT",
                    filter: "agNumberColumnFilter"
                },
                {
                    field: "POSCurr",
                    headerName: "POS Currency",
                    width: 130,
                    enableRowGroup: true,
                    formatType: "CURRENCY",
                    filter: "agTextColumnFilter"
                },
                {
                    field: "POSAmt",
                    headerName: "POS Amount",
                    width: 130,
                    enableRowGroup: true,
                    formatType: "AMOUNT",
                    filter: "agNumberColumnFilter"
                },
                {
                    field: "MerchantInfo",
                    headerName: "Merchant Info",
                    width: 300,
                    enableRowGroup: true,
                    filter: "agTextColumnFilter"
                },
                {
                    field: "MerchID",
                    headerName: "Merchant ID",
                    width: 150,
                    enableRowGroup: true,
                    filter: "agNumberColumnFilter"
                },
                {
                    field: "MerchCategoryCode",
                    headerName: "Merchant Category Code",
                    width: 200,
                    enableRowGroup: true,
                    filter: "agNumberColumnFilter"
                },
                {
                    field: "MerchCategoryName",
                    headerName: "Merchant Category Name",
                    width: 300,
                    enableRowGroup: true,
                    filter: "agTextColumnFilter"
                },
                {
                    field: "ResponseCode",
                    headerName: "Response Code",
                    width: 400,
                    enableRowGroup: true,
                    filter: "agTextColumnFilter"
                },
                {
                    field: "UserReference1",
                    headerName: "User Reference 1",
                    width: 180,
                    enableRowGroup: true,
                    filter: "agNumberColumnFilter"
                },
                {
                    field: "UserReference2",
                    headerName: "User Reference 2",
                    width: 180,
                    enableRowGroup: true,
                    filter: "agTextColumnFilter"
                },
                {
                    field: "UserReference3",
                    headerName: "User Reference 3",
                    width: 180,
                    enableRowGroup: true,
                    filter: "agTextColumnFilter"
                },
                {
                    field: "UserReference4",
                    headerName: "User Reference 4",
                    width: 180,
                    enableRowGroup: true,
                    filter: "agNumberColumnFilter"
                },
                {
                    field: "Notes",
                    headerName: "Notes",
                    width: 200,
                    enableRowGroup: true,
                    filter: "agTextColumnFilter"
                },
                {
                    field: "UserReference5",
                    headerName: "User Reference 5",
                    width: 200,
                    enableRowGroup: true,
                    filter: "agTextColumnFilter"
                },
                {
                    field: "UserReference6",
                    headerName: "User Reference 6",
                    width: 180,
                    enableRowGroup: true,
                    filter: "agNumberColumnFilter"
                },
                {
                    field: "UserReference7",
                    headerName: "User Reference 7",
                    width: 180,
                    enableRowGroup: true,
                    filter: "agTextColumnFilter"
                },
                {
                    field: "UserReference8",
                    headerName: "User Reference 8",
                    width: 180,
                    enableRowGroup: true,
                    filter: "agNumberColumnFilter"
                },
            ];
        }
    }, [activeKey]);

    // Row data
    const rowData = useMemo(() => {
        if (activeKey === "1") {
            // Daily Recon data from JSON file
            return sampleDailyReconData;
        } else if (activeKey === "2") {
            // Fraud Detection Report data from JSON file
            return sampleFraudDetectionData;
        } else {
            // VAN Auth Failure Report data from JSON file
            return sampleAuthFailureData;
        }
    }, [activeKey]);



    // Set up interval for updating values when Daily Recon tab is active
    useEffect(() => {
        // Clear any existing interval
        if (updateIntervalRef.current) {
            clearInterval(updateIntervalRef.current);
            updateIntervalRef.current = null;
        }

        // Only set up interval for Daily Recon tab (activeKey === "1")
        if (activeKey === "1") {
            // Start updating values every 2 seconds (250ms was too fast for demo)
            updateIntervalRef.current = setInterval(() => {
                //updateRandomValues();
            }, 2000);
        }

        // Cleanup on unmount or tab change
        return () => {
            if (updateIntervalRef.current) {
                clearInterval(updateIntervalRef.current);
                updateIntervalRef.current = null;
            }
        };
    }, [activeKey]);

    return (
        <div style={{ height: "100%", display: 'flex', flexDirection: 'column' }}>
            <PageHeader
                title="VCC"
                leftActions={
                    <div style={{ marginLeft: 12 }}>
                        <Tabs
                            items={items}
                            tabBarStyle={{
                                borderBottom: "none",
                                margin: 0,
                            }}
                            activeKey={activeKey}
                            onChange={(tabItem: string) => setActiveKey(tabItem)}
                        />
                    </div>
                }
                showWorkspaceDropdown={false}
            />
            <div style={{ height: '100%' }}>
                <AgTableClient
                    ref={gridRef}
                    //@ts-ignore
                    columnDefs={columnDefs}
                    rowData={rowData}
                    groupPanel
                    defaultPivot={[""]}
                    paginationPageSize={100}
                    pagination={ true}
                //grandTotalRow
                />
            </div>
        </div>
    );
}

